import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, interval, Subject, takeUntil } from 'rxjs';
import { TokenService } from './token.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService implements OnDestroy {
  private readonly tokenService = inject(TokenService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  // Intervalo de verificación: cada 1 minuto
  private readonly CHECK_INTERVAL = 60 * 1000;
  private isRefreshing = false;

  /**
   * Inicializa el servicio de renovación automática de tokens
   * Debe llamarse al iniciar la aplicación
   */
  startTokenRefreshMonitoring(): void {
    // Monitorear cada minuto si el token está por expirar
    interval(this.CHECK_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkAndRefreshToken();
      });

    // También verificar en cada cambio de ruta
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.checkAndRefreshToken();
      });

    // Verificación inicial
    this.checkAndRefreshToken();
  }

  /**
   * Limpia las suscripciones cuando el servicio se destruye
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Verifica si el token está próximo a expirar y lo renueva si es necesario
   */
  private checkAndRefreshToken(): void {
    const accessToken = this.tokenService.getAccessToken();
    const refreshToken = this.tokenService.getRefreshToken();

    // Si no hay tokens, no hacer nada
    if (!accessToken || !refreshToken) {
      return;
    }

    // Si no hay usuario autenticado, no hacer nada
    if (!this.authService.isAuthenticated()) {
      return;
    }

    // Verificar si el refresh token es válido
    if (!this.tokenService.hasValidRefreshToken()) {
      this.tokenService.clearTokens();
      this.authService.logout();
      this.router.navigate(['/login'], {
        queryParams: { reason: 'session_expired' },
      });
      return;
    }

    // Si ya se está refrescando, evitar llamadas duplicadas
    if (this.isRefreshing) {
      return;
    }

    // Verificar si el token está próximo a expirar (menos de 5 minutos)
    if (this.tokenService.isAccessTokenExpiringSoon()) {
      this.refreshToken();
    }
  }

  /**
   * Renueva el access token usando el refresh token
   */
  private refreshToken(): void {
    this.isRefreshing = true;

    this.authService.refreshTokens().subscribe({
      next: () => {
        this.isRefreshing = false;
      },
      error: (error) => {
        this.isRefreshing = false;

        // Solo hacer logout si el error es 401 (Unauthorized)
        if (error.status === 401) {
          this.tokenService.clearTokens();
          this.authService.logout();
          this.router.navigate(['/login'], {
            queryParams: { reason: 'session_expired' },
          });
        }
      },
    });
  }
}
